import { z } from 'zod';

/**
 * The single definition of this app's client environment contract. Kept free of `import.meta.env`:
 * `vite.config.ts` runs in Node, where it does not exist. Both consumers import this schema
 * (`env.ts` validates `import.meta.env`, `vite.config.ts` validates `loadEnv()`), so a missing
 * variable fails the build, not just the running app.
 */
export const clientEnvSchema = {
  /** Base URL of the backend API, e.g. https://api.example.com */
  VITE_API_BASE_URL: z.url(),
  /** Serve MSW browser mocks instead of hitting the real API. */
  VITE_ENABLE_MOCKS: z.stringbool().default(false),
  /** Absent, blank or not a valid DSN disables Sentry entirely (`resolveSentryDsn`). */
  VITE_SENTRY_DSN: z.string().optional(),
  /**
   * OAuth callback target; must match an Allowed callback URL on the app client exactly.
   * Environment-driven rather than derived from the dev port, since every environment serves on a
   * different origin. Other Cognito configuration (pool ID, client ID, domain, scopes) is fetched
   * at runtime from `/v1/identity/config`.
   */
  VITE_COGNITO_REDIRECT_SIGN_IN: z.url(),
  /**
   * Post-logout target; must match an Allowed sign-out URL on the app client exactly. Logout uses
   * Cognito's `logout_uri` form, validated against sign-out URLs, not callback URLs. Must not point
   * at /auth/login: that route redirects straight back to Hosted UI, bouncing logout into a login
   * loop the user can never escape.
   */
  VITE_COGNITO_REDIRECT_SIGN_OUT: z.url(),
} as const;

export const CLIENT_ENV_PREFIX = 'VITE_';
